import React from 'react';
import { formatHelpers } from '../../../utils/apiService';

const UserStatistics = ({ statistics, loading }) => {
    if (loading) {
        return (
            <div className="user-statistics">
                <div className="stats-grid">
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="stat-card loading">
                            <div className="loading-skeleton"></div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (!statistics) {
        return (
            <div className="user-statistics">
                <div className="no-stats">
                    <span className="no-stats-icon">📊</span>
                    <h3>No Statistics Available</h3>
                    <p>Start verifying certificates to see your statistics here.</p>
                </div>
            </div>
        );
    }

    const getSuccessRateColor = (rate) => {
        if (rate >= 80) return 'text-green-600';
        if (rate >= 60) return 'text-yellow-600';
        return 'text-red-600';
    };

    const getSuccessRateIcon = (rate) => {
        if (rate >= 80) return '🎯';
        if (rate >= 60) return '📈';
        return '📉';
    };

    return (
        <div className="user-statistics">
            <div className="section-header">
                <h2 className="section-title">📊 Your Verification Statistics</h2>
                <p className="section-subtitle">
                    Overview of your certificate verification activity
                </p>
            </div>

            <div className="stats-grid">
                {/* Total Verifications */}
                <div className="stat-card blue">
                    <div className="stat-icon">🔍</div>
                    <div className="stat-content">
                        <div className="stat-number">{statistics.total_verifications}</div>
                        <div className="stat-label">Total Verifications</div>
                        <div className="stat-description">
                            Certificates you've verified
                        </div>
                    </div>
                </div>

                {/* Successful Verifications */}
                <div className="stat-card green">
                    <div className="stat-icon">✅</div>
                    <div className="stat-content">
                        <div className="stat-number">{statistics.successful_verifications}</div>
                        <div className="stat-label">Found Certificates</div>
                        <div className="stat-description">
                            Valid certificates verified
                        </div>
                    </div>
                </div>

                {/* Failed Verifications */}
                <div className="stat-card red">
                    <div className="stat-icon">❌</div>
                    <div className="stat-content">
                        <div className="stat-number">{statistics.failed_verifications}</div>
                        <div className="stat-label">Not Found</div>
                        <div className="stat-description">
                            Certificates not in database
                        </div>
                    </div>
                </div>

                {/* Success Rate */}
                <div className="stat-card yellow">
                    <div className="stat-icon">{getSuccessRateIcon(statistics.success_rate)}</div>
                    <div className="stat-content">
                        <div className={`stat-number ${getSuccessRateColor(statistics.success_rate)}`}>
                            {statistics.success_rate}%
                        </div>
                        <div className="stat-label">Success Rate</div>
                        <div className="stat-description">
                            Verification success ratio
                        </div>
                    </div>
                </div>

                {/* Total Amount Spent */}
                <div className="stat-card purple">
                    <div className="stat-icon">💰</div>
                    <div className="stat-content">
                        <div className="stat-number">
                            {formatHelpers.formatCurrency(statistics.total_amount_spent)}
                        </div>
                        <div className="stat-label">Total Spent</div>
                        <div className="stat-description">
                            Verification fees paid
                        </div>
                    </div>
                </div>
            </div>

            {/* Quick Insights */}
            {statistics.total_verifications > 0 && (
                <div className="insights-section">
                    <h3 className="insights-title">💡 Quick Insights</h3>
                    <div className="insights-grid">
                        <div className="insight-card">
                            <div className="insight-icon">📈</div>
                            <div className="insight-content">
                                <h4>Performance</h4>
                                <p>
                                    {statistics.success_rate >= 80
                                        ? "Excellent! Most of your verifications are successful."
                                        : statistics.success_rate >= 60
                                            ? "Good verification rate. Double-check certificate numbers for better results."
                                            : "Consider verifying certificate numbers before payment to improve success rate."
                                    }
                                </p>
                            </div>
                        </div>

                        <div className="insight-card">
                            <div className="insight-icon">💡</div>
                            <div className="insight-content">
                                <h4>Tip</h4>
                                <p>
                                    {statistics.failed_verifications > 0
                                        ? "If certificates aren't found, contact the university registrar to confirm they're in the database."
                                        : "Great job! All your verifications have been successful so far."
                                    }
                                </p>
                            </div>
                        </div>

                        <div className="insight-card">
                            <div className="insight-icon">🎯</div>
                            <div className="insight-content">
                                <h4>Efficiency</h4>
                                <p>
                                    {statistics.total_verifications === 1
                                        ? "Welcome! You've completed your first verification."
                                        : `You've verified ${statistics.total_verifications} certificates. ${formatHelpers.formatCurrency(statistics.total_amount_spent / statistics.total_verifications)} average per verification.`
                                    }
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Progress Bar for Success Rate */}
            {statistics.total_verifications > 0 && (
                <div className="progress-section">
                    <h3 className="progress-title">Success Rate Progress</h3>
                    <div className="progress-bar-container">
                        <div className="progress-bar">
                            <div
                                className={`progress-fill ${statistics.success_rate >= 80 ? 'excellent' :
                                        statistics.success_rate >= 60 ? 'good' : 'needs-improvement'
                                    }`}
                                style={{ width: `${Math.min(statistics.success_rate, 100)}%` }}
                            ></div>
                        </div>
                        <div className="progress-labels">
                            <span>0%</span>
                            <span className="progress-current">
                                {statistics.success_rate}%
                            </span>
                            <span>100%</span>
                        </div>
                    </div>
                    <div className="progress-description">
                        <p>
                            {statistics.success_rate >= 80 && "🎉 Excellent success rate! Keep it up!"}
                            {statistics.success_rate >= 60 && statistics.success_rate < 80 && "👍 Good success rate. Room for improvement."}
                            {statistics.success_rate < 60 && "📚 Consider double-checking certificate numbers before verification."}
                        </p>
                    </div>
                </div>
            )}

            {/* Styles */}
            <style jsx>{`
        .user-statistics {
          margin-bottom: 2rem;
        }

        .section-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .section-title {
          font-size: 1.5rem;
          font-weight: 600;
          color: var(--gray-800);
          margin-bottom: 0.5rem;
        }

        .section-subtitle {
          color: var(--gray-600);
          font-size: 1rem;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .stat-card {
          background: white;
          border-radius: 0.75rem;
          padding: 1.5rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
          border-left: 4px solid transparent;
        }

        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .stat-card.blue { border-left-color: var(--secondary-blue); }
        .stat-card.green { border-left-color: var(--success-green); }
        .stat-card.red { border-left-color: var(--error-red); }
        .stat-card.yellow { border-left-color: var(--warning-yellow); }
        .stat-card.purple { border-left-color: #8b5cf6; }

        .stat-card.loading {
          border-left-color: var(--gray-300);
        }

        .loading-skeleton {
          background: linear-gradient(90deg, var(--gray-200) 25%, var(--gray-100) 50%, var(--gray-200) 75%);
          background-size: 200% 100%;
          animation: loading 1.5s infinite;
          height: 80px;
          border-radius: 0.5rem;
        }

        @keyframes loading {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }

        .stat-icon {
          font-size: 2rem;
          margin-bottom: 1rem;
          display: block;
        }

        .stat-number {
          font-size: 2rem;
          font-weight: 700;
          color: var(--gray-800);
          line-height: 1;
          margin-bottom: 0.5rem;
        }

        .stat-label {
          font-weight: 600;
          color: var(--gray-700);
          margin-bottom: 0.25rem;
        }

        .stat-description {
          font-size: 0.875rem;
          color: var(--gray-500);
        }

        .no-stats {
          text-align: center;
          padding: 3rem 2rem;
          background: white;
          border-radius: 0.75rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .no-stats-icon {
          font-size: 3rem;
          display: block;
          margin-bottom: 1rem;
        }

        .no-stats h3 {
          color: var(--gray-700);
          margin-bottom: 0.5rem;
        }

        .no-stats p {
          color: var(--gray-500);
        }

        .insights-section {
          background: white;
          border-radius: 0.75rem;
          padding: 2rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          margin-bottom: 2rem;
        }

        .insights-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: var(--gray-800);
          margin-bottom: 1.5rem;
          text-align: center;
        }

        .insights-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1rem;
        }

        .insight-card {
          background: var(--gray-50);
          border-radius: 0.5rem;
          padding: 1.5rem;
          display: flex;
          align-items: flex-start;
          gap: 1rem;
        }

        .insight-icon {
          font-size: 1.5rem;
          flex-shrink: 0;
        }

        .insight-content h4 {
          font-size: 1rem;
          font-weight: 600;
          color: var(--gray-800);
          margin-bottom: 0.5rem;
        }

        .insight-content p {
          font-size: 0.875rem;
          color: var(--gray-600);
          line-height: 1.5;
          margin: 0;
        }

        .progress-section {
          background: white;
          border-radius: 0.75rem;
          padding: 2rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .progress-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: var(--gray-800);
          margin-bottom: 1.5rem;
          text-align: center;
        }

        .progress-bar-container {
          margin-bottom: 1rem;
        }

        .progress-bar {
          width: 100%;
          height: 12px;
          background: var(--gray-200);
          border-radius: 6px;
          overflow: hidden;
          margin-bottom: 0.5rem;
        }

        .progress-fill {
          height: 100%;
          border-radius: 6px;
          transition: width 0.8s ease;
          position: relative;
        }

        .progress-fill.excellent {
          background: linear-gradient(90deg, var(--success-green), #10b981);
        }

        .progress-fill.good {
          background: linear-gradient(90deg, var(--warning-yellow), #f59e0b);
        }

        .progress-fill.needs-improvement {
          background: linear-gradient(90deg, var(--error-red), #ef4444);
        }

        .progress-labels {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 0.875rem;
          color: var(--gray-600);
        }

        .progress-current {
          font-weight: 600;
          color: var(--gray-800);
        }

        .progress-description {
          text-align: center;
          margin-top: 1rem;
        }

        .progress-description p {
          font-size: 0.875rem;
          color: var(--gray-600);
          margin: 0;
        }

        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          }

          .insights-grid {
            grid-template-columns: 1fr;
          }

          .stat-card {
            padding: 1rem;
          }

          .stat-number {
            font-size: 1.5rem;
          }

          .stat-icon {
            font-size: 1.5rem;
          }
        }
      `}</style>
        </div>
    );
};

export default UserStatistics;